import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Mail, Calendar, Tag, ArrowRight, X } from 'lucide-react';

const ISSUES = [
  {
    id: 1,
    title: "The Future of Agentic AI in Data Processing",
    date: "December 15, 2024",
    image: "/assets/about_hero_visual_1778832381990.png",
    excerpt: "This week we explore how agentic AI is revolutionizing data processing workflows and what it means for the future of analytics.",
    topics: ["Agentic AI", "Data Processing", "Automation", "Future Trends"],
    content: `This week we explore how agentic AI is revolutionizing data processing workflows and what it means for the future of analytics.

Agentic AI represents a paradigm shift in how we approach data processing. Unlike traditional AI systems that follow predetermined paths, agentic AI can make autonomous decisions, adapt to changing conditions, and optimize workflows in real-time.

Key topics covered in this issue:
• Understanding agentic AI architecture
• Implementation strategies for data workflows
• Case studies from leading enterprises
• Future implications for analytics professionals

This comprehensive analysis provides actionable insights for data teams looking to leverage the next generation of AI technology.`
  },
  {
    id: 2,
    title: "Cross-Database Integration Best Practices",
    date: "December 8, 2024",
    image: "/assets/vibe_coding_pipeline_gen.png",
    excerpt: "Learn how to effectively integrate data across multiple database systems and avoid common pitfalls in cross-platform data management.",
    topics: ["Database Integration", "ETL Pipelines", "Data Migration", "Best Practices"],
    content: `Learn how to effectively integrate data across multiple database systems and avoid common pitfalls in cross-platform data management.

Cross-database integration is becoming increasingly critical as organizations adopt multi-cloud strategies and hybrid data architectures. This comprehensive guide covers:

• Database compatibility assessment
• ETL pipeline design patterns
• Data synchronization strategies
• Performance optimization techniques
• Security considerations
• Migration planning and execution

Real-world case studies demonstrate successful implementations and common challenges faced by enterprise teams.`
  },
  {
    id: 3,
    title: "Optimizing Data Quality for Better Analytics",
    date: "December 1, 2024",
    image: "/assets/vibe_coding_hero.png",
    excerpt: "Discover strategies for maintaining high data quality standards and how clean data leads to more accurate analytics and better business decisions.",
    topics: ["Data Quality", "Data Validation", "Analytics", "Data Governance"],
    content: `Discover strategies for maintaining high data quality standards and how clean data leads to more accurate analytics and better business decisions.

Data quality is the foundation of reliable analytics. Poor data quality can lead to incorrect insights, misguided business decisions, and lost opportunities. This issue explores:

• Data quality assessment frameworks
• Automated validation techniques
• Data cleansing methodologies
• Quality monitoring and alerting
• Governance best practices
• ROI measurement for data quality initiatives

Learn from industry experts who have successfully implemented data quality programs that deliver measurable business value.`
  },
  {
    id: 4,
    title: "Machine Learning Pipeline Optimization",
    date: "November 24, 2024",
    image: "/assets/vibe_coding_code_execution.png",
    excerpt: "Advanced techniques for optimizing machine learning pipelines and improving model performance in production environments.",
    topics: ["Machine Learning", "Pipeline Optimization", "Model Performance", "Production"],
    content: `Advanced techniques for optimizing machine learning pipelines and improving model performance in production environments.

ML pipeline optimization is crucial for maintaining model performance and reducing operational costs. This comprehensive guide covers:

• Pipeline architecture design
• Feature engineering optimization
• Model training acceleration
• Inference performance tuning
• Resource allocation strategies
• Monitoring and maintenance

Case studies from production environments show how teams have achieved significant performance improvements while reducing costs.`
  },
  {
    id: 5,
    title: "Real-Time Data Streaming Architecture",
    date: "November 17, 2024",
    image: "/assets/cdp-pipeline-flow.png",
    excerpt: "Building scalable real-time data streaming architectures for modern applications and IoT systems.",
    topics: ["Real-Time Streaming", "Architecture", "IoT Systems", "Scalability"],
    content: `Building scalable real-time data streaming architectures for modern applications and IoT systems.

Real-time data streaming is essential for modern applications that require immediate insights and responses. This issue explores:

• Streaming architecture patterns
• Technology stack selection
• Scalability considerations
• Data consistency strategies
• Error handling and recovery
• Performance monitoring

Practical examples demonstrate how to build robust streaming systems that can handle high-volume, low-latency data processing requirements.`
  },
  {
    id: 6,
    title: "Cloud Data Warehouse Migration Strategies",
    date: "November 10, 2024",
    image: "/assets/vibe_coding_pipeline_gen.png",
    excerpt: "Comprehensive guide to migrating data warehouses to the cloud with minimal downtime and maximum efficiency.",
    topics: ["Cloud Migration", "Data Warehouse", "Migration Strategy", "Efficiency"],
    content: `Comprehensive guide to migrating data warehouses to the cloud with minimal downtime and maximum efficiency.

Cloud migration is a complex process that requires careful planning and execution. This detailed guide covers:

• Migration planning and assessment
• Data transfer strategies
• Downtime minimization techniques
• Performance optimization
• Cost management
• Security and compliance

Step-by-step migration plans and real-world examples help organizations successfully transition to cloud-based data warehouses.`
  }
];

export default function NewsletterPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeIssue, setActiveIssue] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      toast.success('Thank you for subscribing to our newsletter!');
      setEmail('');
      setLoading(false);
    }, 800);
  };

  return (
    <div className="bg-slate-50 min-h-screen py-16 animate-fade-in font-sans">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight sm:text-5xl">
            Newsletter
          </h1>
          <p className="mt-4 text-xl text-slate-600 max-w-2xl mx-auto">
            Stay Ahead with Weekly AI-Powered Data Insights and Trends.
          </p>
        </div>

        {/* Newsletter Hero Section with Subscription Form */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-3xl p-8 sm:p-12 shadow-sm border border-slate-200/50 mb-16 relative overflow-hidden flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1 text-center md:text-left">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 mb-4">
              <Mail className="w-3 h-3" /> Newsletter
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
              📧 Candor Data Insights
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed max-w-xl">
              Join thousands of data professionals who rely on our newsletter for the latest trends, insights,
              and best practices in data analytics and AI-powered processing.
            </p>
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="flex-grow px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white"
              />
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all hover:-translate-y-0.5 disabled:opacity-50 shadow-md shadow-blue-500/15"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
          <div className="flex-1 w-full max-w-sm aspect-video md:aspect-auto md:h-64 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 shadow-inner">
            <img
              src="/assets/about_hero_visual_1778832381990.png"
              alt="Candor data insights and analytics platform"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/assets/candorlogo.png';
              }}
            />
          </div>
        </div>

        {/* Why Subscribe Section */}
        <div className="mb-20">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-10 text-center">
            Why Subscribe?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm text-center relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Industry Insights</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Stay ahead with the latest trends and developments in data analytics and AI technology.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm text-center relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">💡</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Best Practices</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Learn proven strategies and techniques from industry experts and thought leaders.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm text-center relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Product Updates</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Be the first to know about new features, updates, and releases from Candor Data Platform.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm text-center relative overflow-hidden group hover:shadow-md transition-shadow">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Exclusive Content</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Access subscriber-only content, case studies, and in-depth technical guides.
              </p>
            </div>
          </div>
        </div>

        {/* Recent Issues Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-10 text-center">
            Recent Issues
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ISSUES.map((issue) => (
              <div
                key={issue.id}
                className="bg-white border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col h-full"
              >
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 bg-slate-100 py-1 px-3 rounded-full w-fit font-medium">
                    <Calendar className="w-3.5 h-3.5" />
                    {issue.date}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 leading-snug">
                    {issue.title}
                  </h3>
                  <div className="w-full aspect-[2/1] rounded-xl overflow-hidden border border-slate-100 mb-4 bg-slate-50">
                    <img
                      src={issue.image}
                      alt={issue.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = '/assets/candorlogo.png';
                      }}
                    />
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-grow">
                    {issue.excerpt}
                  </p>
                  
                  <div className="border-t border-slate-100 pt-4 mt-auto">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                      Topics Covered
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {issue.topics.map((topic, i) => (
                        <span
                          key={i}
                          className="bg-slate-50 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-100 border border-slate-200/60 text-slate-600 text-[10px] font-semibold px-2 py-0.5 rounded transition-colors cursor-default"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => setActiveIssue(issue)}
                    className="w-full py-2.5 px-4 rounded-xl text-xs font-semibold text-blue-600 hover:text-blue-700 bg-slate-50 hover:bg-blue-50 border border-slate-200 hover:border-blue-100 transition-all flex items-center justify-center gap-1.5"
                  >
                    Read Full Issue <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Interactive Modal */}
      {activeIssue && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-100 animate-fade-in max-h-[85vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 leading-snug">
                  {activeIssue.title}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> {activeIssue.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Tag className="w-3.5 h-3.5" /> {activeIssue.topics.join(', ')}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setActiveIssue(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="p-6 sm:p-8 overflow-y-auto flex-grow text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">
              {activeIssue.content}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl flex justify-end">
              <button
                onClick={() => setActiveIssue(null)}
                className="px-5 py-2 rounded-xl text-xs font-semibold text-slate-700 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 shadow-sm"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

